import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import apiClient from "@/utils/apiClient";
import downloadFile from "@/utils/fileDownloader";
import { toast } from "sonner";
import {
  Plus,
  Send,
  Calendar,
  Edit2,
  Trash2,
  Filter,
  X,
  Clock,
  AlertCircle,
  Eye,
  Paperclip,
  Download,
  ExternalLink,
  Search,
  User,
  Users,
  CheckCircle2,
  ShieldCheck,
  BookOpen,
  Maximize2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import TinyRichTextEditor from "@/components/shared/TinyRichTextEditor";
import RichTextViewer, { stripHtml } from "@/components/shared/RichTextViewer";

const getAttachmentUrl = (rawUrl) => {
  if (!rawUrl) return '';
  if (typeof rawUrl === 'object' && rawUrl.url) return getAttachmentUrl(rawUrl.url);
  if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) return rawUrl;
  const backendBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  return `${backendBase}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;
};

const getDownloadUrl = (rawUrl) => {
  const url = getAttachmentUrl(rawUrl);
  if (url.includes('/api/v1/common/attachments/')) {
    return url.includes('?') ? `${url}&download=true` : `${url}?download=true`;
  }
  return url;
};

const getCleanFilename = (rawUrl, fallbackIndex = 0) => {
  if (!rawUrl) return `Attachment ${fallbackIndex + 1}`;
  if (typeof rawUrl === 'object' && rawUrl.name) return rawUrl.name;
  try {
    const rawName = decodeURIComponent(String(rawUrl).split('?')[0].split('/').pop() || '');
    const parts = rawName.split('_');
    if (parts.length > 1 && parts[0].length === 12) {
      return parts.slice(1).join('_');
    }
    return rawName || `Attachment ${fallbackIndex + 1}`;
  } catch (e) {
    return `Attachment ${fallbackIndex + 1}`;
  }
};

const AnnouncementsBoard = ({ permissions = {} }) => {
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [myAnnouncements, setMyAnnouncements] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Dialog States
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showStatsDialog, setShowStatsDialog] = useState(false);
  
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [selectedForStats, setSelectedForStats] = useState(null);
  
  const [categoryFilter, setCategoryFilter] = useState("all"); // all, my, general, holidays
  const [assignedStudents, setAssignedStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    message: "",
    category: "general",
    priority: "medium",
    targetType: "all", // all, classes, students
    target_classes: [],
    target_students: [],
  });

  const canSendNotifications = permissions?.can_send_notifications !== false;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch assigned students
      const studentsRes = await apiClient.get(
        "/v1/warden/students/my-students"
      );
      const students = studentsRes.data?.students || [];
      setAssignedStudents(students);

      // Extract unique classes from assigned students
      const uniqueClasses = [
        ...new Set(students.map((s) => s.class_name).filter(Boolean)),
      ];
      setClasses(uniqueClasses.sort());

      // Fetch announcements (general notices)
      await fetchAnnouncements();
      await fetchMyAnnouncements();

      // Fetch holidays
      await fetchHolidays();
    } catch (error) {
      console.log("Failed to load data:", error);
      toast.error("Failed to load notice board");
    } finally {
      setLoading(false);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const response = await apiClient.get("/v1/warden/announcements/");
      setAnnouncements(response.data?.announcements || []);
    } catch (error) {
      console.error("Failed to fetch announcements:", error);
    }
  };

  const fetchMyAnnouncements = async () => {
    try {
      const response = await apiClient.get("/v1/warden/announcements/", {
        params: {
          my_notice: true,
        },
      });
      setMyAnnouncements(response.data?.announcements || []);
    } catch (error) {
      console.error("Failed to fetch my announcements:", error);
    }
  };

  const fetchHolidays = async () => {
    try {
      const response = await apiClient.get(
        "/v1/warden/announcements/holidays/"
      );
      setHolidays(response.data?.holidays || []);
    } catch (error) {
      console.error("Failed to fetch holidays:", error);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.message.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (
      formData.targetType === "classes" &&
      formData.target_classes.length === 0
    ) {
      toast.error("Please select at least one class");
      return;
    }

    if (
      formData.targetType === "students" &&
      formData.target_students.length === 0
    ) {
      toast.error("Please select at least one student");
      return;
    }

    try {
      const storedUser = JSON.parse(localStorage.getItem("hostelconnect_user") || "{}");
      const hostelId = user?.hostel_id || user?.hostelId || storedUser?.hostel_id;

      const payload = {
        title: formData.title,
        message: formData.message,
        category: formData.category,
        priority: formData.priority,
        hostel_id: hostelId,
        target_all: formData.targetType === "all",
        target_classes:
          formData.targetType === "classes" ? formData.target_classes : [],
        target_students:
          formData.targetType === "students" ? formData.target_students : [],
        send_now: true,
      };

      const response = await apiClient.post(
        "/v1/warden/announcements/create",
        payload
      );
      toast.success(
        `Notice sent to ${response.data.recipients_count || "all"} recipients!`
      );

      resetForm();
      setShowCreateDialog(false);
      fetchAnnouncements();
      fetchMyAnnouncements();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create notice");
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.message.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await apiClient.put(
        `/v1/warden/announcements/${selectedAnnouncement.id}`,
        {
          title: formData.title,
          message: formData.message,
          priority: formData.priority,
        }
      );

      toast.success("Notice updated successfully");
      setShowEditDialog(false);
      setSelectedAnnouncement(null);
      resetForm();
      fetchAnnouncements();
      fetchMyAnnouncements();
      if (selectedNotice && selectedNotice.id === selectedAnnouncement?.id) {
        setSelectedNotice(prev => ({
          ...prev,
          title: formData.title,
          message: formData.message,
          priority: formData.priority
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update notice");
    }
  };

  const handleDelete = async (announcementId) => {
    if (!window.confirm("Are you sure you want to delete this notice?")) {
      return;
    }

    try {
      await apiClient.delete(`/v1/warden/announcements/${announcementId}`);
      toast.success("Notice deleted successfully");
      if (showDetailDialog && selectedNotice?.id === announcementId) {
        setShowDetailDialog(false);
      }
      fetchAnnouncements();
      fetchMyAnnouncements();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete notice");
    }
  };

  const fetchStats = async (announcement) => {
    setSelectedForStats(announcement);
    setShowStatsDialog(true);
    setLoadingStats(true);
    setStats(null);
    try {
      const response = await apiClient.get(`/v1/warden/announcements/${announcement.id}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
      toast.error("Failed to load read receipts");
    } finally {
      setLoadingStats(false);
    }
  };

  const openEditDialog = (announcement) => {
    setSelectedAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      message: announcement.message,
      category: announcement.category || "general",
      priority: announcement.priority || "medium",
      targetType: "all",
      target_classes: announcement.target_classes || [],
      target_students: announcement.target_students || [],
    });
    setShowEditDialog(true);
  };

  const handleOpenNoticeDetail = (item) => {
    setSelectedNotice(item);
    setShowDetailDialog(true);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      message: "",
      category: "general",
      priority: "medium",
      targetType: "all",
      target_classes: [],
      target_students: [],
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return "bg-red-100 text-red-800 border-red-200";
      case "medium":
        return "bg-amber-100 text-amber-800 border-amber-200";
      case "low":
        return "bg-blue-100 text-blue-800 border-blue-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const filteredItems = () => {
    let list = [];
    if (categoryFilter === "all") {
      list = [
        ...announcements,
        ...holidays.map((h) => ({ ...h, category: "holidays" })),
      ];
    } else if (categoryFilter === "my") {
      list = myAnnouncements;
    } else if (categoryFilter === "general") {
      list = announcements;
    } else if (categoryFilter === "holidays") {
      list = holidays.map((h) => ({ ...h, category: "holidays" }));
    }

    if (!searchQuery.trim()) return list;

    const query = searchQuery.toLowerCase();
    return list.filter((item) => {
      const title = (item.title || item.name || "").toLowerCase();
      const message = (item.message || item.description || "").toLowerCase();
      const holidayType = (item.holiday_type || "").toLowerCase();
      return title.includes(query) || message.includes(query) || holidayType.includes(query);
    });
  };

  const sortedItems = filteredItems().sort((a, b) => {
    const dateA = new Date(a.created_at || a.start_date || 0);
    const dateB = new Date(b.created_at || b.start_date || 0);
    return dateB - dateA;
  });

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-3">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-gray-500">Loading notice board...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto animate-fade-in" data-testid="warden-notice-board">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-blue-600" />
            Notice Board
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            View detailed official notices, hostel updates, holidays, and broadcasts for your assigned students
          </p>
        </div>

        {canSendNotifications && (
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold gap-2 shadow-md shrink-0"
          >
            <Plus className="w-4 h-4" />
            Create General Notice
          </Button>
        )}
      </div>

      {/* Permission Warning */}
      {!canSendNotifications && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
          <p className="text-sm text-yellow-800 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            You have view-only access. Contact admin to enable notice creation permissions.
          </p>
        </div>
      )}

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4">
        {/* Category Filters */}
        <div className="flex gap-2 flex-wrap bg-slate-100/80 p-1.5 rounded-xl text-sm font-medium">
          <button
            onClick={() => setCategoryFilter("all")}
            className={`px-3.5 py-1.5 rounded-lg transition-all ${
              categoryFilter === "all"
                ? "bg-white text-slate-800 shadow-sm font-bold"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            All Notices ({announcements.length + holidays.length})
          </button>
          
          <button
            onClick={() => setCategoryFilter("my")}
            className={`px-3.5 py-1.5 rounded-lg transition-all ${
              categoryFilter === "my"
                ? "bg-white text-purple-700 shadow-sm font-bold"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            My Notices ({myAnnouncements.length})
          </button>

          <button
            onClick={() => setCategoryFilter("general")}
            className={`px-3.5 py-1.5 rounded-lg transition-all ${
              categoryFilter === "general"
                ? "bg-white text-blue-700 shadow-sm font-bold"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            General ({announcements.length})
          </button>

          <button
            onClick={() => setCategoryFilter("holidays")}
            className={`px-3.5 py-1.5 rounded-lg transition-all ${
              categoryFilter === "holidays"
                ? "bg-white text-emerald-700 shadow-sm font-bold"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Holidays ({holidays.length})
          </button>
        </div>

        {/* Search Input */}
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <input
            type="text"
            placeholder="Search notices, keywords..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Notices List */}
      <div className="space-y-4">
        {sortedItems.length === 0 ? (
          <Card className="border-dashed p-10 text-center">
            <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-700 font-semibold">No notices found</p>
            <p className="text-slate-400 text-xs mt-1">There are no notices matching your filter or search criteria.</p>
          </Card>
        ) : (
          sortedItems.map((item) => {
            const isHoliday = item.category === "holidays";
            const isOwnNotice = item.created_by === (user.user_id || user.id);

            return (
              <Card 
                key={item.id || item._id} 
                className="border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all overflow-hidden cursor-pointer group bg-white"
                onClick={() => handleOpenNoticeDetail(item)}
              >
                <CardContent className="p-5">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div className="flex-1 space-y-2">
                      {/* Top Badges */}
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`px-2.5 py-0.5 text-xs rounded-full font-bold uppercase tracking-wider border ${
                            isHoliday
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : getPriorityColor(item.priority || "medium")
                          }`}
                        >
                          {isHoliday ? "Holiday" : `${item.priority || "Medium"} Priority`}
                        </span>

                        {isHoliday && item.holiday_type && (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                            {item.holiday_type}
                          </Badge>
                        )}

                        {isOwnNotice && !isHoliday && (
                          <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded-full font-semibold">
                            Created by you
                          </span>
                        )}

                        {!isOwnNotice && !isHoliday && (
                          <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-700 rounded-full font-medium flex items-center gap-1">
                            <ShieldCheck className="w-3 h-3 text-blue-600" /> Official Admin Notice
                          </span>
                        )}
                      </div>

                      {/* Notice Title */}
                      <h2 className="text-lg font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                        {item.title || item.name}
                      </h2>

                      {/* Snippet */}
                      <p className="text-slate-600 text-sm line-clamp-2 leading-relaxed">
                        {stripHtml(item.message || item.description) ||
                          `${formatDate(item.start_date)} to ${formatDate(item.end_date)}`}
                      </p>

                      {/* Metadata Row */}
                      <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400 pt-1">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5 text-slate-400" />
                          {isHoliday
                            ? `${formatDate(item.start_date)} - ${formatDate(item.end_date)}`
                            : formatDate(item.created_at)}
                        </span>

                        {isHoliday && item.total_days && (
                          <span className="font-semibold text-emerald-700">Duration: {item.total_days} Days</span>
                        )}

                        {item.target_classes?.length > 0 && (
                          <span className="flex items-center gap-1 text-slate-600 font-medium">
                            <Users className="w-3 h-3 text-blue-600" />
                            Classes: {item.target_classes.join(", ")}
                          </span>
                        )}

                        {item.target_all && (
                          <span className="text-slate-600 font-medium">
                            Target: All Hostel Parents
                          </span>
                        )}

                        {item.attachments && item.attachments.length > 0 && (
                          <span className="flex items-center gap-1 text-purple-700 font-semibold bg-purple-50 px-2 py-0.5 rounded">
                            <Paperclip className="w-3 h-3" />
                            {item.attachments.length} {item.attachments.length === 1 ? 'Attachment' : 'Attachments'}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Right side Action Buttons */}
                    <div 
                      className="flex items-center gap-2 self-end md:self-center shrink-0 pt-2 md:pt-0"
                      onClick={(e) => e.stopPropagation()} // Prevent card click when clicking action buttons
                    >
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenNoticeDetail(item)}
                        className="text-blue-600 border-blue-200 hover:bg-blue-50 text-xs font-semibold gap-1.5"
                      >
                        <Eye className="w-3.5 h-3.5" /> View Full Notice
                      </Button>

                      {isOwnNotice && !isHoliday && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => fetchStats(item)}
                            className="text-slate-700 hover:bg-slate-100 text-xs"
                            title="Read Receipts & Recipient Stats"
                          >
                            <Users className="w-3.5 h-3.5 text-blue-600" />
                          </Button>
                          
                          {canSendNotifications && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEditDialog(item)}
                                className="text-slate-700 hover:bg-slate-100 text-xs"
                                title="Edit Notice"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDelete(item.id)}
                                className="text-red-600 hover:bg-red-50 border-red-200 text-xs"
                                title="Delete Notice"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Elaborate Notice Detail Dialog Modal */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedNotice && (
            <div className="space-y-6 py-2">
              <DialogHeader className="space-y-2">
                {/* Badges */}
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`px-2.5 py-0.5 text-xs rounded-full font-bold uppercase tracking-wider border ${
                      selectedNotice.category === "holidays"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : getPriorityColor(selectedNotice.priority || "medium")
                    }`}
                  >
                    {selectedNotice.category === "holidays"
                      ? "Holiday"
                      : `${selectedNotice.priority || "Medium"} Priority`}
                  </span>

                  {selectedNotice.category === "holidays" && selectedNotice.holiday_type && (
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      {selectedNotice.holiday_type}
                    </Badge>
                  )}

                  {selectedNotice.created_by === (user.user_id || user.id) ? (
                    <span className="px-2.5 py-0.5 text-xs bg-purple-100 text-purple-800 rounded-full font-bold">
                      Created by you
                    </span>
                  ) : (
                    <span className="px-2.5 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full font-bold flex items-center gap-1">
                      <ShieldCheck className="w-3.5 h-3.5" /> Official Notice
                    </span>
                  )}
                </div>

                {/* Title */}
                <DialogTitle className="text-2xl font-bold text-slate-900 leading-tight">
                  {selectedNotice.title || selectedNotice.name}
                </DialogTitle>

                {/* Metadata details */}
                <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 pt-1 border-b border-slate-100 pb-3">
                  <span className="flex items-center gap-1.5 font-medium text-slate-600">
                    <Clock className="w-4 h-4 text-blue-600" />
                    Published: {formatDate(selectedNotice.created_at || selectedNotice.start_date)}
                  </span>

                  {selectedNotice.created_by_name && (
                    <span className="flex items-center gap-1 font-medium text-slate-600">
                      <User className="w-4 h-4 text-slate-500" />
                      Sender: {selectedNotice.created_by_name} ({selectedNotice.created_by_role || 'Admin'})
                    </span>
                  )}
                </div>
              </DialogHeader>

              {/* Holiday Summary Info Card if Holiday */}
              {selectedNotice.category === "holidays" && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                  <div>
                    <span className="font-bold text-emerald-900 block">📅 Start Date:</span>
                    <span className="text-emerald-800 font-semibold">{formatDate(selectedNotice.start_date)}</span>
                  </div>
                  <div>
                    <span className="font-bold text-emerald-900 block">📅 End Date:</span>
                    <span className="text-emerald-800 font-semibold">{formatDate(selectedNotice.end_date)}</span>
                  </div>
                  <div>
                    <span className="font-bold text-emerald-900 block">⏳ Total Days:</span>
                    <span className="text-emerald-800 font-semibold">{selectedNotice.total_days || 'N/A'} Days</span>
                  </div>
                </div>
              )}

              {/* Target Audience Scope Box */}
              {!selectedNotice.category && (selectedNotice.target_all || selectedNotice.target_classes?.length > 0 || selectedNotice.target_students?.length > 0) && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-xs space-y-1.5">
                  <span className="font-bold text-slate-700 flex items-center gap-1.5 uppercase tracking-wider text-[11px]">
                    <Users className="w-3.5 h-3.5 text-blue-600" /> Target Audience
                  </span>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {selectedNotice.target_all && (
                      <span className="px-2.5 py-1 bg-white border border-slate-200 rounded-md text-slate-700 font-semibold">
                        All Assigned Students & Parents
                      </span>
                    )}
                    {selectedNotice.target_classes?.map((c, i) => (
                      <span key={i} className="px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-md font-semibold">
                        Class: {c}
                      </span>
                    ))}
                    {selectedNotice.target_students?.length > 0 && (
                      <span className="px-2.5 py-1 bg-purple-50 text-purple-700 border border-purple-200 rounded-md font-semibold">
                        {selectedNotice.target_students.length} Specific Students
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Elaborate Rich Text Body */}
              <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 text-slate-800 text-sm leading-relaxed">
                <RichTextViewer content={selectedNotice.message || selectedNotice.description} />
              </div>

              {/* Full Attachments Viewer with View and Download */}
              {selectedNotice.attachments?.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-bold text-xs text-slate-700 flex items-center gap-2 uppercase tracking-wider">
                    <Paperclip className="w-4 h-4 text-blue-600" />
                    Attached Files ({selectedNotice.attachments.length})
                  </h4>
                  <div className="grid gap-2">
                    {selectedNotice.attachments.map((att, idx) => {
                      const filename = getCleanFilename(att, idx);
                      const viewUrl = getAttachmentUrl(att);
                      const downloadUrl = getDownloadUrl(att);
                      return (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-xl hover:border-blue-300 transition-colors shadow-xs"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
                              <Paperclip className="w-4 h-4" />
                            </div>
                            <span className="text-sm font-medium text-slate-800 truncate max-w-[280px]">
                              {filename}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <a
                              href={viewUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                              View
                            </a>
                            <button
                              type="button"
                              onClick={() => downloadFile(downloadUrl, filename)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                            >
                              <Download className="w-3.5 h-3.5" />
                              Download
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Footer Actions */}
              <div className="flex flex-wrap items-center justify-between gap-2 pt-4 border-t border-slate-100">
                <div className="flex items-center gap-2">
                  {selectedNotice.created_by === (user.user_id || user.id) && selectedNotice.category !== "holidays" && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowDetailDialog(false);
                          fetchStats(selectedNotice);
                        }}
                        className="text-blue-700 border-blue-200 hover:bg-blue-50 text-xs font-semibold gap-1.5"
                      >
                        <Users className="w-4 h-4 text-blue-600" /> Read Receipts & Stats
                      </Button>

                      {canSendNotifications && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setShowDetailDialog(false);
                              openEditDialog(selectedNotice);
                            }}
                            className="text-slate-700 hover:bg-slate-100 text-xs font-medium gap-1"
                          >
                            <Edit2 className="w-3.5 h-3.5" /> Edit Notice
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(selectedNotice.id)}
                            className="text-red-600 hover:bg-red-50 border-red-200 text-xs font-medium gap-1"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </Button>
                        </>
                      )}
                    </>
                  )}
                </div>

                <Button 
                  onClick={() => setShowDetailDialog(false)} 
                  className="bg-slate-800 hover:bg-slate-900 text-white text-xs font-bold"
                >
                  Close Notice
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Notice Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-800 flex items-center gap-2">
              <Plus className="w-5 h-5 text-blue-600" />
              Create General Notice
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 pt-2">
            <div>
              <Label className="font-bold text-xs text-slate-600">Notice Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="e.g. Evening Study Hours Schedule Update"
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label className="mb-1.5 block font-bold text-xs text-slate-600">Notice Message (Rich Text) *</Label>
              <TinyRichTextEditor
                value={formData.message}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, message: value }))
                }
                placeholder="Write your notice message in full detail..."
                minHeight="150px"
                required
              />
            </div>

            <div>
              <Label className="font-bold text-xs text-slate-600">Priority</Label>
              <select
                className="w-full mt-1 px-3 py-2 border rounded-xl text-sm bg-slate-50 border-slate-200"
                value={formData.priority}
                onChange={(e) =>
                  setFormData({ ...formData, priority: e.target.value })
                }
              >
                <option value="low">Low Priority</option>
                <option value="medium">Medium Priority</option>
                <option value="high">High Priority</option>
              </select>
            </div>

            <div>
              <Label className="font-bold text-xs text-slate-600">Send Notice To (Audience Scope)</Label>
              <select
                className="w-full mt-1 px-3 py-2 border rounded-xl text-sm bg-slate-50 border-slate-200"
                value={formData.targetType}
                onChange={(e) =>
                  setFormData({ ...formData, targetType: e.target.value })
                }
              >
                <option value="all">All Assigned Students' Parents</option>
                <option value="classes">Specific Classes (from my students)</option>
                <option value="students">Specific Students (from my students)</option>
              </select>
            </div>

            {formData.targetType === "classes" && (
              <div>
                <Label className="font-bold text-xs text-slate-600">Select Classes</Label>
                <div className="border border-slate-200 rounded-xl p-3 space-y-2 max-h-40 overflow-y-auto mt-1 bg-slate-50">
                  {classes.map((className) => (
                    <label key={className} className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={formData.target_classes.includes(className)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              target_classes: [
                                ...formData.target_classes,
                                className,
                              ],
                            });
                          } else {
                            setFormData({
                              ...formData,
                              target_classes: formData.target_classes.filter(
                                (c) => c !== className
                              ),
                            });
                          }
                        }}
                      />
                      <span>{className}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {formData.targetType === "students" && (
              <div>
                <Label className="font-bold text-xs text-slate-600">Select Students</Label>
                <div className="border border-slate-200 rounded-xl p-3 space-y-2 max-h-60 overflow-y-auto mt-1 bg-slate-50">
                  {assignedStudents.map((student) => (
                    <label key={student.id} className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={formData.target_students.includes(student.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              target_students: [
                                ...formData.target_students,
                                student.id,
                              ],
                            });
                          } else {
                            setFormData({
                              ...formData,
                              target_students: formData.target_students.filter(
                                (id) => id !== student.id
                              ),
                            });
                          }
                        }}
                      />
                      <span>
                        {student.name} - {student.class_name} ({student.readable_id})
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <DialogFooter className="gap-2 pt-3 border-t border-slate-100">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowCreateDialog(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-bold gap-2">
                <Send className="w-4 h-4" />
                Send Notice
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Notice Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-800 flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-blue-600" />
              Edit Notice
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4 pt-2">
            <div>
              <Label className="font-bold text-xs text-slate-600">Notice Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label className="mb-1.5 block font-bold text-xs text-slate-600">Notice Message (Rich Text) *</Label>
              <TinyRichTextEditor
                value={formData.message}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, message: value }))
                }
                placeholder="Notice message..."
                minHeight="150px"
                required
              />
            </div>

            <div>
              <Label className="font-bold text-xs text-slate-600">Priority</Label>
              <select
                className="w-full mt-1 px-3 py-2 border rounded-xl text-sm bg-slate-50 border-slate-200"
                value={formData.priority}
                onChange={(e) =>
                  setFormData({ ...formData, priority: e.target.value })
                }
              >
                <option value="low">Low Priority</option>
                <option value="medium">Medium Priority</option>
                <option value="high">High Priority</option>
              </select>
            </div>

            <DialogFooter className="gap-2 pt-3 border-t border-slate-100">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowEditDialog(false);
                  setSelectedAnnouncement(null);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-bold">
                Update Notice
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stats Dialog Modal */}
      <Dialog open={showStatsDialog} onOpenChange={setShowStatsDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-800 font-bold">
              <Eye className="w-5 h-5 text-blue-600" />
              Read Receipts: {selectedForStats?.title}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {selectedForStats?.message && (
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 text-sm">
                <RichTextViewer content={selectedForStats.message} />
              </div>
            )}

            {selectedForStats?.attachments && selectedForStats.attachments.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-bold text-xs text-slate-700 flex items-center gap-1.5 uppercase tracking-wider">
                  <Paperclip className="w-3.5 h-3.5 text-blue-600" />
                  Attachments ({selectedForStats.attachments.length})
                </h4>
                <div className="grid gap-2">
                  {selectedForStats.attachments.map((att, idx) => {
                    const name = getCleanFilename(att, idx);
                    const viewUrl = getAttachmentUrl(att);
                    const downloadUrl = getDownloadUrl(att);
                    return (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-2.5 bg-white border border-slate-200 rounded-lg text-xs"
                      >
                        <span className="font-medium text-slate-800 truncate max-w-[260px]">
                          {name}
                        </span>
                        <div className="flex items-center gap-1.5 ml-2">
                          <a
                            href={viewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-slate-100 text-slate-700 hover:bg-slate-200"
                          >
                            <ExternalLink className="w-3 h-3" />
                            View
                          </a>
                          <button
                            type="button"
                            onClick={() => downloadFile(downloadUrl, name)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                          >
                            <Download className="w-3 h-3" />
                            Download
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {loadingStats ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-4">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-sm text-slate-500">Loading recipient status...</p>
              </div>
            ) : stats ? (
              <div className="space-y-6">
                {/* Summary Card */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200 text-center">
                    <p className="text-2xl font-bold text-emerald-700">{stats.read_count}</p>
                    <p className="text-xs text-emerald-600 uppercase font-bold tracking-wider">Already Read</p>
                  </div>
                  <div className="bg-rose-50 p-4 rounded-xl border border-rose-200 text-center">
                    <p className="text-2xl font-bold text-rose-700">{Math.max(0, stats.total_count - stats.read_count)}</p>
                    <p className="text-xs text-rose-600 uppercase font-bold tracking-wider">Not Read Yet</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Unread Section */}
                  {stats.unread?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold text-rose-600 uppercase tracking-widest mb-3 flex items-center gap-2">
                        <span className="w-2 h-2 bg-rose-600 rounded-full"></span>
                        Pending Recipients ({stats.unread.length})
                      </h4>
                      <div className="grid gap-2 max-h-[250px] overflow-y-auto pr-2 custom-scrollbar">
                        {stats.unread.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-white border border-rose-100 rounded-lg shadow-sm">
                            <div>
                              <p className="font-bold text-slate-800">{item.student_name}</p>
                              <p className="text-[10px] text-slate-500">Class: {item.class}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs font-medium text-rose-600 italic">Parent: {item.parent_name}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Read Section */}
                  {stats.read?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold text-emerald-600 uppercase tracking-widest mb-3 flex items-center gap-2">
                        <span className="w-2 h-2 bg-emerald-600 rounded-full"></span>
                        Already Read ({stats.read.length})
                      </h4>
                      <div className="grid gap-2 max-h-[250px] overflow-y-auto pr-2 custom-scrollbar">
                        {stats.read.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-white border border-emerald-100 rounded-lg shadow-sm">
                            <div>
                              <p className="font-bold text-slate-800">{item.student_name}</p>
                              <p className="text-[10px] text-slate-500">Class: {item.class}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs font-bold text-emerald-600">Parent: {item.parent_name}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                No recipient data found for this notice.
              </div>
            )}
            
            <div className="pt-4 flex justify-end">
              <Button onClick={() => setShowStatsDialog(false)} className="bg-slate-800 hover:bg-slate-900 text-white">
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnnouncementsBoard;
